import { useState } from "react";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRecordContext,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { RelativeDate } from "../misc/RelativeDate";
import { MARKETING_PATHS, touchesPath } from "./paths";
import type { ContactWithLifecycle, Engagement } from "./types";
import { invalidateTouched, todayLocal } from "./touchActions";
import {
  dateInputValue,
  deleteTouch,
  touchPermission,
  updateTouch,
} from "./touchEdit";
import {
  describeEngagement,
  engagementListParams,
  lastOutreachAt,
  notePreview,
  TOUCH_HISTORY_EXPANDED_SIZE,
  TOUCH_HISTORY_PAGE_SIZE,
} from "./touchLog";

/**
 * The touch log, read back.
 *
 * WHY A DERIVED CLOCK NEEDS A VISIBLE LOG
 * ----------------------------------------
 * `contacts.last_contacted_at` is computed by one database trigger as
 * max(occurred_at) over outreach engagements, and everything downstream --
 * contacts_overdue, contacts_stalled, the Monday digest -- is computed from
 * it. Until this component existed, none of that evidence appeared anywhere.
 *
 * A number you cannot audit is not trusted for long. The first time the digest
 * says a contact is stale and you remember calling them, there has to be a
 * screen that settles it -- otherwise you conclude the digest is unreliable,
 * stop opening it, and the failure this system was built to prevent comes back
 * with better typography.
 *
 * WHY THE CONTACT PAGE SHOWS ONLY THREE
 * --------------------------------------
 * This panel sits above the notes feed, so every row it adds pushes the rest
 * of the record down. A contact worked for a year would turn their own page
 * into a scrolling log and bury the thing you opened it for. Three answers
 * "have they been contacted lately?" at a glance; the rest has its own page.
 */
export const TouchHistory = () => {
  const contact = useRecordContext<ContactWithLifecycle>();

  const { data, total, isPending, error } = useGetList<Engagement>(
    "engagements",
    engagementListParams(contact?.id ?? 0, TOUCH_HISTORY_PAGE_SIZE),
    { enabled: contact?.id != null },
  );

  if (!contact) return null;

  const hidden = (total ?? 0) - (data?.length ?? 0);

  return (
    <div className="mt-4">
      <TouchHeader engagements={data} isPending={isPending} error={error} />

      <TouchList data={data} isPending={isPending} error={error} />

      {hidden > 0 && (
        <Button
          asChild
          variant="link"
          size="sm"
          className="mt-1 h-auto p-0 text-xs text-muted-foreground underline hover:no-underline"
        >
          <Link to={touchesPath(contact.id)}>
            See all {total} {total === 1 ? "touch" : "touches"}
          </Link>
        </Button>
      )}
    </div>
  );
};

/**
 * Every touch for one contact, on its own page.
 *
 * Reached from the contact panel. A separate route rather than an expanding
 * panel because a long history deserves its own scroll position, its own
 * back button, and a URL somebody can send to someone else.
 */
export const ContactTouches = () => {
  const { id } = useParams();

  const { data: contact } = useGetOne<ContactWithLifecycle>(
    "contacts",
    { id: id! },
    { enabled: !!id },
  );

  const { data, total, isPending, error } = useGetList<Engagement>(
    "engagements",
    engagementListParams(id ?? 0, TOUCH_HISTORY_EXPANDED_SIZE),
    { enabled: !!id },
  );

  const name = contact
    ? `${contact.first_name} ${contact.last_name}`.trim()
    : "this contact";

  return (
    <div className="max-w-3xl">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="-ml-2 mb-2 text-muted-foreground"
      >
        <Link to={id ? `/contacts/${id}/show` : MARKETING_PATHS.hub}>
          <ArrowLeft className="size-4" /> Back to {name}
        </Link>
      </Button>

      <h1 className="text-xl font-semibold">Touch history</h1>
      <p className="text-sm text-muted-foreground">
        Everything recorded for {name}
        {typeof total === "number" ? ` — ${total} in total` : ""}. Newest first,
        by when contact happened rather than when it was typed.
      </p>

      <div className="mt-4">
        <TouchHeader
          engagements={data}
          isPending={isPending}
          error={error}
          showTitle={false}
        />
        {/* full: the page has nothing below it to protect, so notes render
            whole and wrapped rather than making someone click to read what
            the page is entirely about. */}
        <TouchList data={data} isPending={isPending} error={error} full />
      </div>
    </div>
  );
};

/* ── shared pieces ─────────────────────────────────────────────────────── */

const TouchHeader = ({
  engagements,
  isPending,
  error,
  showTitle = true,
}: {
  engagements?: Engagement[];
  isPending: boolean;
  error?: unknown;
  /** The dedicated page already has an h1 saying this; two would be noise. */
  showTitle?: boolean;
}) => {
  /*
   * "Last contacted" comes from the TOP OUTREACH ROW, not from
   * contact.last_contacted_at, and not from the first row either.
   *
   * The column never arrives: the data provider rewrites getOne("contacts") to
   * getOne("contacts_summary"), an upstream VIEW that lists its columns
   * explicitly and predates 048, so the record on a contact page has 21
   * columns and `last_contacted_at` is not among them. Reading it yields undefined, and
   * the header would say "Never contacted" about every contact in the
   * database, in confident bold text, above a list of their touches.
   *
   * And not `data[0]`, because the newest row may be a click -- them, not us.
   * Printing its date would have the page say "Last contacted yesterday" about
   * someone the digest still, correctly, calls three weeks overdue.
   *
   * lastOutreachAt over a list already sorted occurred_at DESC IS the
   * max(occurred_at) the trigger computes. Headline and evidence are one fact
   * read once, and cannot drift.
   */
  const lastContacted = lastOutreachAt(engagements ?? []);

  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        {showTitle ? (
          <h3 className="text-sm font-semibold">Touch history</h3>
        ) : (
          <span />
        )}
        <span className="text-xs text-muted-foreground">
          {/* Silent while loading or failed: "Never contacted" is a claim, and
              a claim must not be made from an answer we do not have. */}
          {error || isPending ? null : lastContacted ? (
            <>
              Last contacted <RelativeDate date={lastContacted} />
            </>
          ) : (
            "Never contacted"
          )}
        </span>
      </div>
      <Separator className="mt-1.5" />
    </>
  );
};

const TouchList = ({
  data,
  isPending,
  error,
  full = false,
}: {
  data?: Engagement[];
  isPending: boolean;
  error?: unknown;
  /** Show whole notes, wrapped. The dedicated page has the room. */
  full?: boolean;
}) => {
  /*
   * An error must not look like an empty list. If RLS or the network denies
   * the read, "No touches yet" is a confident false statement about a person's
   * history -- the one thing this screen exists to never do.
   */
  if (error)
    return (
      <p className="mt-3 text-sm text-destructive">
        Could not load the touch history, so this is not a record of nothing
        having happened — it is a record of not knowing.
      </p>
    );
  if (isPending)
    return <p className="mt-3 text-sm text-muted-foreground">Loading…</p>;
  if (!data?.length)
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        No touches recorded. This contact counts as never contacted and will
        keep appearing in the stalled list until something is logged.
      </p>
    );

  return (
    <ul className="mt-3 space-y-2">
      {data.map((engagement) => (
        <TouchRow key={engagement.id} engagement={engagement} full={full} />
      ))}
    </ul>
  );
};

const TouchRow = ({
  engagement,
  full = false,
}: {
  engagement: Engagement;
  full?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(false);
  const { label, origin, countsAsOutreach } = describeEngagement(engagement);
  const { text, truncated } = notePreview(engagement.note);

  return (
    <li className="group flex gap-3 text-sm">
      {/*
        occurred_at, not created_at. This is when contact HAPPENED -- a touch
        logged today for last Tuesday's call reads "last Tuesday" here, and
        that is also the value the cadence clock uses.
      */}
      <span className="w-28 shrink-0 text-xs text-muted-foreground pt-0.5">
        <RelativeDate date={engagement.occurred_at} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="leading-snug">
          <span
            className={cn(
              "font-medium",
              !countsAsOutreach && "text-muted-foreground italic",
            )}
          >
            {label}
          </span>
          <span className="text-xs text-muted-foreground"> · {origin}</span>
          {/*
            Said out loud, because it is surprising and it is the whole reason
            these rows look different. An open or a click is them, not us, so
            it is recorded but does not delay our follow-up -- otherwise the
            most interested person on the list would be the one we chase least.
          */}
          {!countsAsOutreach && (
            <span className="text-xs text-muted-foreground">
              {" "}
              · them, not us — doesn&apos;t reset the clock
            </span>
          )}
        </p>

        {/*
          THE PAGE WRAPS, THE PANEL TRUNCATES.

          On the contact page this sits above the notes feed, so a long note
          has to stop somewhere or it pushes the record off screen -- there,
          the preview is a button and the whole thing opens in a dialog.

          The dedicated page has nothing below it to protect. Making someone
          click through to read what a page is entirely about is a click that
          buys nothing, so it renders whole, wrapped, newlines intact.
        */}
        {full
          ? engagement.note && (
              <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                {engagement.note}
              </p>
            )
          : text &&
            (truncated ? (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="block w-full truncate text-left text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                title="Show the whole note"
              >
                {text}
              </button>
            ) : (
              <p className="truncate text-xs text-muted-foreground">{text}</p>
            ))}

        {!full && truncated && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{label}</DialogTitle>
                <DialogDescription>
                  {origin} · {new Date(engagement.occurred_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              {/*
                whitespace-pre-wrap: the preview flattened newlines to make one
                honest line, and this is where the real shape comes back.
                break-words so a pasted URL cannot widen the dialog.
              */}
              <p className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words text-sm">
                {engagement.note}
              </p>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/*
        ONE control, not two.

        A trash icon beside a pencil, on every row of a list, is a permanent
        opportunity to destroy a record while aiming at the one next to it --
        and the rows are 28px apart. Deleting now lives inside the dialog the
        pencil opens, which means reaching it takes a deliberate second step
        and you can see the whole touch before you remove it.

        The button is never disabled. A grayed-out icon says "no" without
        saying why; opening the dialog on a Resend row shows the touch and
        explains that it records what the contact did. Always rendered rather
        than revealed on hover, because a control that only exists on hover
        does not exist on a touchscreen.
      */}
      <div className="flex shrink-0 opacity-40 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          title="Open this touch"
          aria-label={`Open ${label}`}
          onClick={() => setDetail(true)}
        >
          <Pencil className="size-3.5" />
        </Button>
      </div>

      <TouchDetailDialog
        engagement={engagement}
        open={detail}
        onOpenChange={setDetail}
      />
    </li>
  );
};

const EDIT_KINDS = [
  { id: "call", name: "Call" },
  { id: "email", name: "Email" },
  { id: "met", name: "Met" },
  { id: "other", name: "Other" },
] as const;

/**
 * One dialog for one touch: read it, change it, or remove it.
 *
 * WHY REMOVE LIVES IN HERE
 * ------------------------
 * It used to be a trash icon on the row, next to the pencil, on every row of
 * a list whose rows are 28px apart -- a standing invitation to delete the
 * record below the one you meant. Now it takes opening the touch first, which
 * costs one deliberate click and shows you exactly what you are about to
 * remove.
 *
 * WHY IT OPENS EVEN WHEN NOTHING CAN BE CHANGED
 * ---------------------------------------------
 * A disabled pencil communicates "no" and nothing else. A Resend row opens
 * here, shows its contents, and says why it cannot be edited: it records what
 * the contact did, and there is no true version of "they opened it, but
 * differently". The refusal is more useful than the absence.
 *
 * The confirm step replaces this dialog's contents rather than stacking a
 * second dialog on top -- nested modals are how people lose track of which
 * question they are answering.
 */
const TouchDetailDialog = ({
  engagement,
  open,
  onOpenChange,
}: {
  engagement: Engagement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const permission = touchPermission(engagement);
  const { label, origin } = describeEngagement(engagement);

  const [kind, setKind] = useState<string>(engagement.kind);
  const [note, setNote] = useState(engagement.note ?? "");
  const [date, setDate] = useState(dateInputValue(engagement.occurred_at));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const close = (next: boolean) => {
    onOpenChange(next);
    // Reopening must not land mid-confirmation.
    if (!next) setConfirming(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await updateTouch(dataProvider, engagement, { kind, note, date });
      await invalidateTouched(queryClient);
      notify("Touch updated.", { type: "success" });
      close(false);
    } catch (e) {
      notify((e as Error).message ?? "Could not update the touch", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteTouch(dataProvider, engagement);
      await invalidateTouched(queryClient);
      // Unticking the note changes the note too, and the notes feed sits
      // directly below this panel showing the same record.
      if (permission.deleteVia === "note")
        await queryClient.invalidateQueries({ queryKey: ["contact_notes"] });
      // The ask is the same everywhere; the report is specific, because what
      // happened to the note is worth knowing and the note is still visible
      // in the feed below.
      notify(
        permission.deleteVia === "note"
          ? "Touch removed — the note stays, marked as not contact."
          : "Touch removed.",
        { type: "success" },
      );
      close(false);
    } catch (e) {
      notify((e as Error).message ?? "Could not remove the touch", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const viaNote = permission.deleteVia === "note";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        {confirming ? (
          <>
            <DialogHeader>
              <DialogTitle>Remove this touch?</DialogTitle>
              <DialogDescription>
                {/*
                  Say which of the two operations this is. They are genuinely
                  different acts: unticking the note is the only version that
                  stays done, because deleting the engagement row directly
                  reappears the next time that note is edited.
                */}
                {viaNote
                  ? "This touch comes from a note. The note stays and is marked as not contact, so the touch is removed and will not come back."
                  : "This removes the record that contact happened."}{" "}
                Their follow-up clock is recalculated, so they may become due
                again.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Back
              </Button>
              <Button variant="destructive" onClick={remove} disabled={busy}>
                {busy ? "Removing…" : "Remove"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{label}</DialogTitle>
              <DialogDescription>
                {origin} · {new Date(engagement.occurred_at).toLocaleString()}
              </DialogDescription>
            </DialogHeader>

            {permission.canEdit ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={kind} onValueChange={setKind}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EDIT_KINDS.map((k) => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`edit-date-${engagement.id}`}>Date</Label>
                  <Input
                    id={`edit-date-${engagement.id}`}
                    type="date"
                    value={date}
                    max={todayLocal()}
                    onChange={(e) => setDate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    When contact happened, not when it was recorded — changing
                    it moves this person&apos;s follow-up clock with it.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`edit-note-${engagement.id}`}>Note</Label>
                  <Textarea
                    id={`edit-note-${engagement.id}`}
                    rows={5}
                    placeholder="What happened…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {engagement.note && (
                  <p className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words text-sm">
                    {engagement.note}
                  </p>
                )}
                {/* The refusal, with its reason. More useful than a disabled
                    icon, which says "no" and stops there. */}
                <p className="text-xs text-muted-foreground">
                  {permission.why}
                </p>
              </div>
            )}

            <DialogFooter className="sm:justify-between">
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={!permission.canDelete || busy}
                title={permission.canDelete ? undefined : permission.why}
                onClick={() => setConfirming(true)}
              >
                <Trash2 className="size-4" />
                {/*
                  "Remove" for every touch, including the ones removed by
                  unticking a note rather than deleting a row.

                  This said "Not contact" on note-derived touches, naming the
                  mechanism. A user asked why one row's button differed from
                  the others, which is the whole objection: they clicked the
                  same control on the same list wanting the same outcome --
                  this touch out of the history -- and the difference is in how
                  we achieve it, not in what they asked for. Label the intent;
                  explain the consequence in prose, where there is room to be
                  accurate about it.
                */}
                Remove
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => close(false)}
                  disabled={busy}
                >
                  {permission.canEdit ? "Cancel" : "Close"}
                </Button>
                {permission.canEdit && (
                  <Button onClick={save} disabled={busy || !date}>
                    {busy ? "Saving…" : "Save"}
                  </Button>
                )}
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
